/**
 * API helper – talks to the Ayrine backend (Express)
 * ──────────────────────────────────────────────────
 * All Razorpay + Shiprocket calls go through the backend to keep secrets safe.
 */

const API_BASE = process.env.NODE_ENV === 'development' ? 'http://localhost:5000' : (process.env.REACT_APP_BACKEND_URL || "https://ayrine-backend-r8y7.onrender.com");

async function request(path, options = {}) {
  const url = `${API_BASE}${path}`;
  const res = await fetch(url, {
    headers: { "Content-Type": "application/json", ...options.headers },
    ...options,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

// ─────────────── Razorpay ───────────────

/** Create a Razorpay order on the backend. Returns { order, key_id } */
export async function createRazorpayOrder({ amount, receipt, notes }) {
  return request("/api/razorpay/create-order", {
    method: "POST",
    body: JSON.stringify({ amount, receipt, notes }),
  });
}

// Removed createRazorpayOrderWithShipping and getShippingPreview

/** Verify Razorpay payment signature on the backend */
export async function verifyRazorpayPayment({ razorpay_order_id, razorpay_payment_id, razorpay_signature }) {
  return request("/api/razorpay/verify-payment", {
    method: "POST",
    body: JSON.stringify({ razorpay_order_id, razorpay_payment_id, razorpay_signature }),
  });
}

/** Open the Razorpay Checkout modal and return a promise that resolves with payment details */
export function openRazorpayCheckout({ order, key_id, customerInfo, onDismiss }) {
  return new Promise((resolve, reject) => {
    const loadScript = () => {
      if (window.Razorpay) return Promise.resolve(true);
      return new Promise((res) => {
        const script = document.createElement("script");
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.onload = () => res(true);
        script.onerror = () => res(false);
        document.body.appendChild(script);
      });
    };

    loadScript().then((loaded) => {
      if (!loaded) {
        reject(new Error("Razorpay SDK failed to load. Please check your internet connection."));
        return;
      }

      const options = {
        key: key_id,
        amount: order.amount,
        currency: order.currency || "INR",
        name: "Ayrine",
        description: "Order Payment",
        order_id: order.id,
        prefill: {
          name: customerInfo?.name || "",
          email: customerInfo?.email || "",
          contact: customerInfo?.phone || "",
        },
        theme: { color: "#d4af37" },
        handler: function (response) {
          resolve(response);
        },
        modal: {
          ondismiss: function () {
            if (onDismiss) onDismiss();
            reject(new Error("Payment cancelled by user"));
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", function (response) {
        reject(new Error(response.error?.description || "Payment failed"));
      });
      rzp.open();
    });
  });
}

// ─────────────── Shiprocket ───────────────

/** Create a Shiprocket shipment order */
export async function createShiprocketOrder(orderData) {
  return request("/api/shiprocket/create-order", {
    method: "POST",
    body: JSON.stringify(orderData),
  });
}

/** Track a Shiprocket order by order ID */
export async function trackShipment(orderId) {
  return request(`/api/shiprocket/track/${orderId}`);
}

/** Track a Shiprocket shipment by AWB number */
export async function trackByAWB(awb) {
  return request(`/api/shiprocket/track-awb/${awb}`);
}

/** Check if a pincode is serviceable */
export async function checkServiceability({ pickup_postcode, delivery_postcode, weight, cod }) {
  return request("/api/shiprocket/check-serviceability", {
    method: "POST",
    body: JSON.stringify({ pickup_postcode, delivery_postcode, weight, cod }),
  });
}

/** Get Shiprocket order details */
export async function getShiprocketOrder(orderId) {
  return request(`/api/shiprocket/order/${orderId}`);
}

/** Cancel a Shiprocket order */
export async function cancelShiprocketOrder(orderIds) {
  return request("/api/shiprocket/cancel-order", {
    method: "POST",
    body: JSON.stringify({ ids: Array.isArray(orderIds) ? orderIds : [orderIds] }),
  });
}
